import { useEffect, useState } from "react";

const STATS_ENDPOINT =
  process.env.REACT_APP_STATS_ENDPOINT || "/project/api/stats";

const FALLBACK_STATS = {
  users: 479520,
  accountsClosed: 3155079,
  solRefunded: 6434.09,
  price: 0.001127,
  mcap: 1093963.98,
  holders: 17981,
  staked: 60.55,
  burned: 2.93,
  circulatingSupply: 0,
  totalSupply: 1_000_000_000,
};

// Module-level singleton so multiple components don't start multiple pollers.
const shared = {
  data: FALLBACK_STATS,
  loading: true,
  error: null,
  inFlight: null,
  intervalId: null,
  listeners: new Set(),
};

const notify = () => {
  for (const cb of shared.listeners) cb();
};

const normalizeStats = (data) => ({
  users: data.totalUsers ?? data.users ?? FALLBACK_STATS.users,
  accountsClosed:
    data.totalClosedAccounts ?? data.accountsClosed ?? FALLBACK_STATS.accountsClosed,
  solRefunded: data.solRefunded ?? FALLBACK_STATS.solRefunded,
  price: data.price ?? FALLBACK_STATS.price,
  mcap: data.marketCap ?? data.mcap ?? FALLBACK_STATS.mcap,
  holders: data.holders ?? FALLBACK_STATS.holders,
  // Percent values
  staked: data.totalStakedPercent ?? data.staked ?? FALLBACK_STATS.staked,
  burned: data.percentBurned ?? data.burned ?? FALLBACK_STATS.burned,
  circulatingSupply: data.circulatingSupply ?? FALLBACK_STATS.circulatingSupply,
  totalSupply: data.totalSupply ?? FALLBACK_STATS.totalSupply,
});

const fetchSharedStats = async () => {
  if (shared.inFlight) return shared.inFlight;

  shared.inFlight = (async () => {
    try {
      const res = await fetch(STATS_ENDPOINT);
      if (!res.ok) throw new Error(`Stats request failed: ${res.status}`);

      const payload = await res.json();
      const data = payload?.data || payload;

      shared.data = normalizeStats(data || {});
      shared.error = null;
    } catch (err) {
      shared.error = err?.message || String(err);
      // Keep last known good shared.data; if none, fallback.
      shared.data = shared.data || FALLBACK_STATS;
    } finally {
      shared.loading = false;
      shared.inFlight = null;
      notify();
    }
  })();

  return shared.inFlight;
};

const startPollingIfNeeded = () => {
  if (shared.intervalId) return;
  fetchSharedStats();
  shared.intervalId = setInterval(fetchSharedStats, 10000);
};

const stopPollingIfIdle = () => {
  if (shared.listeners.size > 0) return;
  if (shared.intervalId) clearInterval(shared.intervalId);
  shared.intervalId = null;
};

export default function useFetchStats() {
  const [state, setState] = useState(() => ({
    data: shared.data,
    loading: shared.loading,
    error: shared.error,
  }));

  useEffect(() => {
    const onUpdate = () => {
      setState({
        data: shared.data,
        loading: shared.loading,
        error: shared.error,
      });
    };

    shared.listeners.add(onUpdate);
    startPollingIfNeeded();
    onUpdate();

    return () => {
      shared.listeners.delete(onUpdate);
      stopPollingIfIdle();
    };
  }, []);

  return state;
}
